import React, { useState, useMemo } from 'react';
import { Typography, Box, Button, Alert, Snackbar } from '@mui/material';
import NewFilterDrawer from '../../components/shared/FilterDrawer/NewFilterDrawer';
import FILTER_CONSTANTS, { FILTER_VALUES, FILTER_CONTENT_TYPE } from '../../constants/FilterConstants';
import Breadcrumbs from '../../components/shared/Breadcrumbs/Breadcrumbs';
import BREADCRUMB_NAMES from '../../constants/breadcrumbNames';
import { routeConstants } from '../../constants/routeConstants';
import useStyles from './style';
import NayaxTrackingsTable from './NayaxTrackingsTable';
import { Modal, Dropzone, GridContainer, GridItem } from '../../components/shared';
import { IN_PAGE_NOTIFICATION_TYPES } from '../../constants/InPageNotificationTypes';
import { useUploadNayaxCsvMutation } from '../../services/Nayax/NayaxService';
// import SearchBar from '../../components/SearchBar';

const NayaxFeed = () => {
  const classes = useStyles();
  const [filterParams, setFilterParams] = useState({
    dateRange: { from: '', to: '' },
  });
  const [searchQuery] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dropZoneKey, setDropZoneKey] = useState(0);
  const [fileErrors, setFileErrors] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [, setFileColumnCounts] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [uploadAbortController, setUploadAbortController] = useState(null);

  const [uploadNayaxCsv, { isLoading: isUploading }] = useUploadNayaxCsvMutation();

  const { DRAWER_TYPES, TITLE } = FILTER_CONSTANTS;

  const NAYAX_INITIAL_PARAMS = {
    [FILTER_VALUES.DATE_RANGE]: { from: '', to: '' },
  };

  const NAYAX_FILTER = [
    {
      type: FILTER_CONTENT_TYPE.DATE_RANGE_PICKER,
      name: 'Date Range',
      value: FILTER_VALUES.DATE_RANGE,
      rangeKey: 'submitted',
    },
  ];

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const MIN_REQUIRED_COLUMNS = 24;

  const {
    SETTINGS: { NAYAX_FEED, SETTINGS },
  } = BREADCRUMB_NAMES;

  const breadcrumbData = useMemo(
    () => [{ text: SETTINGS, redirection: routeConstants.SETTING_ROUTE }, { text: NAYAX_FEED }],
    [],
  );

  const resetFilterParams = () => {
    setFilterParams({
      [FILTER_VALUES.DATE_RANGE]: { from: '', to: '' },
    });
  };

  const onApplyFilterHandler = (appliedFilters) => setFilterParams(appliedFilters);

  // const handleSearch = (query) => {
  //   setSearchQuery(query);
  //   setRefreshTrigger((prev) => prev + 1);
  // };

  // const handleSearchReset = () => {
  //   setSearchQuery('');
  //   setRefreshTrigger((prev) => prev + 1);
  // };

  const handleOpenModal = () => setOpenModal(true);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFiles([]);
    setFileErrors([]);
    setFileColumnCounts({});
    setWarnings([]);
    setDropZoneKey((prevValue) => prevValue + 1);
  };

  const handleClearFiles = () => {
    if (uploadAbortController) {
      uploadAbortController.abort();
      setUploadAbortController(null);
      openSnackbar('Upload cancelled', 'warning');
    } else {
      openSnackbar('Files cleared', 'info');
    }

    setSelectedFiles([]);
    setFileErrors([]);
    setFileColumnCounts({});
    setWarnings([]);
    setDropZoneKey((prevValue) => prevValue + 1);
  };

  const disableClear = selectedFiles.length === 0 && fileErrors.length === 0 && warnings.length === 0;

  const openSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const parseCsvLine = (line) => {
    const cols = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        cols.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    cols.push(current);
    return cols;
  };

  const parseCsvSample = (text, maxSampleLines = 10) => {
    if (!text) {
      return {
        columnCount: null,
        warnings: ['File is empty.'],
      };
    }

    if (text.charCodeAt(0) === 0xfeff) {
      text = text.slice(1);
    }

    const rawLines = text
      .split(/\r?\n/)
      .map((l) => l.replace(/\r$/, ''))
      .filter((l) => l.trim().length > 0);

    const lines = rawLines.slice(0, maxSampleLines);
    const parsed = lines.map((l) => ({ line: l, cols: parseCsvLine(l) }));

    if (!parsed.length) {
      return {
        columnCount: null,
        warnings: ['File appears to be empty.'],
      };
    }

    const header = parsed[0];
    const firstData = parsed.slice(1).find((p) => p.cols.some((c) => c.trim() !== ''));

    const headerCount = header.cols.length;
    const dataCount = firstData ? firstData.cols.length : headerCount;

    const warnings = [];
    if (firstData && Math.abs(dataCount - headerCount) > 1) {
      warnings.push(
        `Header has ${headerCount} columns but first data line has ${dataCount}. This might indicate a formatting issue.`,
      );
    }

    return {
      columnCount: headerCount,
      warnings,
    };
  };

  const validateCsvStructure = (columnCount) => {
    if (columnCount == null || columnCount < 1) {
      return 'Invalid CSV file: No columns detected.';
    }
    if (columnCount < MIN_REQUIRED_COLUMNS) {
      return 'Column count is not matching.';
    }
    return '';
  };

  const validateFile = (file) =>
    new Promise((resolve) => {
      const errors = [];
      const warnings = [];

      if (file.size > MAX_FILE_SIZE) {
        errors.push('File is too large. Maximum allowed size is 15 MB.');
        resolve({ errors, warnings, columnCount: null });
        return;
      }

      if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
        errors.push('Please select a .csv file.');
        resolve({ errors, warnings, columnCount: null });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result || '';
          const meta = parseCsvSample(text, 10);

          if (meta.columnCount == null) {
            errors.push('Unable to determine column count (empty file?).');
          } else {
            const structureError = validateCsvStructure(meta.columnCount);
            if (structureError) {
              errors.push(structureError);
            }
          }

          warnings.push(...meta.warnings);
          resolve({ errors, warnings, columnCount: meta.columnCount });
        } catch (parseError) {
          errors.push('Could not read the CSV file.');
          resolve({ errors, warnings, columnCount: null });
        }
      };

      const CHUNK_SIZE = 128 * 1024;
      reader.readAsText(file.slice(0, CHUNK_SIZE));
    });

  const onFileChange = async (files, rejectedFiles) => {
    const allErrors = [];
    const allWarnings = [];
    const newFileColumnCounts = {};

    if (rejectedFiles?.length > 0) {
      rejectedFiles.forEach((rejectedFile) => {
        const { file, errors: fileErrors } = rejectedFile;
        fileErrors.forEach((error) => {
          const errorMap = {
            'file-invalid-type': 'Invalid file type. Only CSV files are allowed.',
            'file-too-large': 'File size too large. Maximum 15MB allowed.',
          };
          allErrors.push(`${file.name}: ${errorMap[error.code] || error.message}`);
        });
      });
    }

    if (files?.length > 0) {
      const fileValidationPromises = files.map(async (file) => {
        const { errors, warnings, columnCount } = await validateFile(file);

        if (errors.length > 0) {
          errors.forEach((error) => allErrors.push(`${file.name}: ${error}`));
        } else if (columnCount !== null) {
          newFileColumnCounts[file.name] = columnCount;
        }

        warnings.forEach((warning) => allWarnings.push(`${file.name}: ${warning}`));
      });

      await Promise.all(fileValidationPromises);
    }

    setSelectedFiles(allErrors.length === 0 ? files : []);
    setFileErrors(allErrors);
    setFileColumnCounts(newFileColumnCounts);
    setWarnings(allWarnings);

    if (allErrors.length > 0) {
      openSnackbar(`${allErrors.length} file(s) rejected. Check details below.`, 'error');
    } else if (files?.length) {
      openSnackbar(`${files.length} file(s) added and validated.`, 'success');
    }
  };

  const handleImport = async () => {
    if (selectedFiles.length > 0) {
      try {
        const controller = new AbortController();
        setUploadAbortController(controller);

        await uploadNayaxCsv(selectedFiles).unwrap();

        setUploadAbortController(null);
        openSnackbar(`Successfully uploaded ${selectedFiles.length} file(s). Processing started.`, 'success');
        handleCloseModal();
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        setUploadAbortController(null);
        if (error.name === 'AbortError') {
          openSnackbar('Upload was cancelled', 'warning');
        } else {
          const errorMessage = error?.data?.message || error?.message || 'Upload failed. Please try again.';
          openSnackbar(errorMessage, 'error');
        }
      }
    }
  };

  const disableImport = selectedFiles.length === 0 || fileErrors.length > 0 || isUploading;

  const modalActionButtons = (
    <Box>
      <Button variant="contained" onClick={handleImport} disabled={disableImport} sx={{ mr: 1 }}>
        {isUploading ? 'Uploading...' : 'Upload'}
      </Button>
      <Button variant="outlined" onClick={handleClearFiles} disabled={disableClear} sx={{ mr: 1 }}>
        {isUploading ? 'Cancel Upload' : 'Cancel'}
      </Button>
    </Box>
  );

  return (
    <>
      <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      <Typography mt={'34px'} variant="h1">
        Nayax Feed
      </Typography>

      <Box className={classes.searchFilterWrap}>
        {/* <Box className={classes.searchWrap}>
          <SearchBar onSearch={handleSearch} onReset={handleSearchReset} isEnterDisabled={false} />
        </Box> */}
        <Box />

        <Box pl={3} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            sx={{
              color: 'red',
              fontWeight: 'bold',
              border: '1px solid red',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: '#fff8f8',
                border: '1px solid red',
              },
            }}
            onClick={handleOpenModal}
            data-testid="nayax-upload-btn"
          >
            Upload Files
          </Button>
          <NewFilterDrawer
            filteredValues={filterParams}
            name={TITLE.FILTERS.toUpperCase()}
            title={TITLE.FILTERS}
            drawerType={DRAWER_TYPES.FILTER_DRAWER}
            onReset={resetFilterParams}
            onApplyFilter={onApplyFilterHandler}
            filterData={NAYAX_FILTER}
            emptyFilterLength={JSON.stringify(NAYAX_INITIAL_PARAMS).length}
          />
        </Box>
      </Box>

      <NayaxTrackingsTable filterParams={filterParams} searchQuery={searchQuery} refreshTrigger={refreshTrigger} />

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        title="Upload Nayax Report"
        titleVariant="h3"
        closeIcon={true}
        maxWidth="sm"
        actionButtons={modalActionButtons}
      >
        <GridContainer direction="column" spacing={2}>
          <GridItem xs={12}>
            <Dropzone
              key={dropZoneKey}
              acceptedFiles={['.csv']}
              maxFileSize={Number.MAX_SAFE_INTEGER}
              onChange={onFileChange}
              multiple={true}
              filesLimit={1}
              disabled={isUploading}
            />
          </GridItem>

          <GridItem xs={12}>
            <Alert severity={IN_PAGE_NOTIFICATION_TYPES.INFO}>The maximum allowed file size is 20MB</Alert>
            <Alert severity={IN_PAGE_NOTIFICATION_TYPES.INFO}>
              Only files with the following formats are allowed: CSV
            </Alert>
          </GridItem>

          {fileErrors.length > 0 && (
            <GridItem xs={12}>
              {fileErrors.map((error, index) => (
                <Alert
                  key={index}
                  severity={IN_PAGE_NOTIFICATION_TYPES.ERROR}
                  sx={{ mb: index < fileErrors.length - 1 ? 1 : 0 }}
                >
                  {error}
                </Alert>
              ))}
            </GridItem>
          )}

          {warnings.length > 0 && (
            <GridItem xs={12}>
              {warnings.map((warning, index) => (
                <Alert
                  key={index}
                  severity={IN_PAGE_NOTIFICATION_TYPES.WARNING}
                  sx={{ mb: index < warnings.length - 1 ? 1 : 0 }}
                >
                  {warning}
                </Alert>
              ))}
            </GridItem>
          )}

          {selectedFiles.length > 0 && fileErrors.length === 0 && (
            <GridItem xs={12}>
              <Alert severity={IN_PAGE_NOTIFICATION_TYPES.SUCCESS}>
                {selectedFiles.length} file(s) selected for import and validated successfully.
              </Alert>
            </GridItem>
          )}

          {isUploading && (
            <GridItem xs={12}>
              <Alert severity={IN_PAGE_NOTIFICATION_TYPES.INFO}>Uploading files... Please wait.</Alert>
            </GridItem>
          )}
        </GridContainer>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NayaxFeed;
